import {
  OptionGroupDef,
  SectionGroupDef,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  PhysicalROSTemplateTemplateTypeEnum,
  RosTemplateTemplateTypesEnum,
} from "@/services";
import {
  DateGroupDef,
  LibraryTemplateDef,
  InitialF2fExtraFields,
} from "./initial-f2f-types";
import { COGNITIVE_STATUS_GROUPS } from "./initial-f2f-cognitive-constant";
import { MINI_COG_DEFAULT_VALUE } from "./initial-f2f-mini-cog-constant";
import { FUNCTIONAL_HOMEBOUND_GROUPS } from "./initial-f2f-objective-constant";
import {
  ADVANCE_DIRECTIVE_GROUPS,
  FACE_TO_FACE_GROUPS,
  HOME_HEALTH_ORDER_GROUPS,
  PATIENT_INSTRUCTION_GROUPS,
  START_OF_CARE_GROUPS,
} from "./initial-f2f-plan-text-constant";
import { PLAN_OF_CARE_GROUPS } from "./initial-f2f-plan-constant";

export const INITIAL_F2F_SECTIONS: SectionGroupDef[] = [
  {
    heading: "Subjective",
    sections: [
      { key: "chiefComplaints", title: "Chief Complaint" },
      { key: "hpi", title: "History of Present Illness" },
      {
        key: "hospitalization",
        title: "List of Hospitalization",
      },
      { key: "medicalHistory", title: "Medical History" },
      { key: "surgicalHistory", title: "Surgical History" },
      { key: "familyHistory", title: "Family History" },
      { key: "socialHistory", title: "Social History" },
      { key: "allergy", title: "Allergy" },
      {
        key: "medication",
        title: "Medication List & Pharmacy",
      },
      { key: "lastPcp", title: "Last PCP Visit and Labs" },
      {
        key: "specialist",
        title: "Current Medical Specialist",
      },
    ],
  },
  {
    heading: "Objective",
    sections: [
      { key: "reviewOfSystems", title: "Review of Systems" },
      { key: "vitalSigns", title: "Vital Signs" },
      { key: "physicalExam", title: "Physical Exam" },
      { key: "miniCog", title: "Mini Cognitive Test" },
      { key: "cognitiveStatus", title: "Cognitive Status" },
      { key: "homebound", title: "Functional Status" },
    ],
  },
  {
    heading: "Assessment",
    sections: [
      { key: "diagnosis", title: "Diagnosis" },
      { key: "screening", title: "eCareHealth Assessments" },
    ],
  },
  {
    heading: "Plan",
    sections: [
      { key: "planOfCare", title: "Plan of Care" },
      { key: "advanceDirectives", title: "Advance Directives" },
      {
        key: "patientInstructions",
        title: "Patient Instructions",
      },
      { key: "faceToFace", title: "Face to Face Encounter" },
      { key: "homeHealthOrder", title: "Home Health Order" },
      { key: "startOfCare", title: "Start of Care" },
    ],
  },
  {
    heading: "Home Health Assessments",
    sections: [
      {
        key: "annualWellness",
        title: "Annual Wellness",
      },
      { key: "diabeticScreening", title: "Diabetic Screening" },
      { key: "smokingScreening", title: "Smoking Screening" },
      { key: "obesityScreening", title: "Obesity Screening" },
    ],
  },
  {
    heading: "Procedure Codes",
    sections: [{ key: "procedureCodes", title: "Procedure Codes" }],
  },
];

export const INITIAL_F2F_SECTION_KEYS: string[] = INITIAL_F2F_SECTIONS.flatMap(
  (group) => group.sections.map((section) => section.key)
);

export const LIBRARY_TEMPLATE_SECTIONS: Record<
  "reviewOfSystems" | "physicalExam",
  LibraryTemplateDef
> = {
  reviewOfSystems: {
    templateType: RosTemplateTemplateTypesEnum.HomeCareRos,
    templateName: "Home Care (ROS)",
    encounterTemplateType: PhysicalROSTemplateTemplateTypeEnum.Ros,
  },
  physicalExam: {
    templateType: RosTemplateTemplateTypesEnum.HomeCarePe,
    templateName: "Home Care (PE)",
    encounterTemplateType: PhysicalROSTemplateTemplateTypeEnum.PhysicalExam,
  },
};

export const ROS_PE_LIST_KEY = "patient-ros-pe-template-list";
export const ROS_PE_PAGE_SIZE = 50;
export const ROS_PE_SAVE_LABEL = "Save";
export const ROS_PE_MISSING_APPOINTMENT_MESSAGE =
  "Open this note from an appointment to save.";

export const INITIAL_F2F_EXTRA_DEFAULTS: InitialF2fExtraFields = {
  diagnosisCodes: [],
  diagnosisFreeText: "",
  cptCodes: [],
  cptFreeText: "",
  screeningCustom: [],
  screeningFreeText: "",
  sectionOptions: {},
  sectionDates: {},
  miniCog: MINI_COG_DEFAULT_VALUE,
};

export interface OptionSectionDef {
  groups: OptionGroupDef[];
  hint?: string;
  withCptCodes?: boolean;
  dateGroup?: DateGroupDef;
}

export const PLAN_OF_CARE_HEADING = "PLAN OF CARE";

export const INITIAL_F2F_OPTION_SECTIONS: Record<string, OptionSectionDef> = {
  homebound: { groups: FUNCTIONAL_HOMEBOUND_GROUPS },
  cognitiveStatus: { groups: COGNITIVE_STATUS_GROUPS },
  planOfCare: { groups: PLAN_OF_CARE_GROUPS, withCptCodes: true },
  advanceDirectives: { groups: ADVANCE_DIRECTIVE_GROUPS },
  patientInstructions: { groups: PATIENT_INSTRUCTION_GROUPS },
  faceToFace: { groups: FACE_TO_FACE_GROUPS },
  homeHealthOrder: { groups: HOME_HEALTH_ORDER_GROUPS },
  startOfCare: { groups: START_OF_CARE_GROUPS },
};

export const NOTE_CONTENT_SECTION_KEYS = {
  hpi: "hpi",
  hospitalization: "hospitalization",
  medication: "medication",
  pharmacy: "preferred_pharmacy",
  medicalHistory: "medical_history",
  surgicalHistory: "surgical_history",
  familyHistory: "family_history",
  allergy: "allergy",
  socialHistory: "social_history",
  lastPcp: "last_pcp_visit_labs",
  specialist: "current_medical_specialist",
  miniCog: "mini_cognitive_test",
  cognitiveStatus: "cognitive_status",
  homebound: "functional_homebound_status",
  diagnosis: "diagnosis",
  screening: "screening",
  vitalSigns: "vital_signs",
  planOfCare: "plan_of_care",
  advanceDirectives: "advance_directives",
  patientInstructions: "patient_instructions",
  faceToFace: "face_to_face_encounter",
  homeHealthOrder: "home_health_order",
  startOfCare: "start_of_care",
} as const;

export const NOTE_HISTORY_FREE_TEXT_SECTIONS = [
  "medicalHistory",
  "surgicalHistory",
  "familyHistory",
] as const;

export const NOTE_CONTENT_OPTION_SECTIONS = [
  "cognitiveStatus",
  "homebound",
  "planOfCare",
  "advanceDirectives",
  "patientInstructions",
  "faceToFace",
  "homeHealthOrder",
  "startOfCare",
] as const;

export const NOTE_ANSWER_KEY_OVERRIDES: Record<string, string> = {
  medicalConditionAffectingLeavingHome: "homebound_q1",
  inabilityToLeaveHome: "homebound_q2",
  considerableAndTaxingEffort: "homebound_q3",
};

export const NOTE_ROW_ANSWER_KEYS: Record<string, string> = {
  "Patient denies": "denies",
  "Patient was hospitalized": "hospitalized",
  "Reason of hospitalization": "reason",
};

export const NOTE_FREE_TEXT_ANSWER_KEY = "free_text";
export const NOTE_PHARMACIES_ANSWER_KEY = "pharmacies";
export const NOTE_DIAGNOSIS_CODES_ANSWER_KEY = "icd_codes";
export const NOTE_DIAGNOSIS_FREE_TEXT_ANSWER_KEY = "diagnosis_free_text";
export const NOTE_SCREENING_CUSTOM_ANSWER_KEY = "custom_assessments";
export const NOTE_SCREENING_FREE_TEXT_ANSWER_KEY = "screening_free_text";
export const NOTE_CPT_CODES_ANSWER_KEY = "cpt_codes";
export const NOTE_CPT_FREE_TEXT_ANSWER_KEY = "cpt_code_free_text";
export const NOTE_TEXT_SEPARATOR = " ";
// Rows joined as "Label: value, Label: value" instead of dot-terminated sentences.
export const NOTE_ROW_LIST_SEPARATOR = ", ";
export const NOTE_TRAILING_DOT = /\.$/;
export const NOTE_VITAL_DETAIL_SEPARATOR = " — ";
// BMI is derived from height and weight at sign-off so the report can print it.
export const NOTE_BMI_ANSWER_KEY = "bmi";
export const NOTE_BMI_LABEL = "BMI";
export const NOTE_BMI_UNIT = "kg/m²";
export const NOTE_BMI_EMPTY = "—";
